import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { AuthUser } from '@/types';
import { authApi } from '@/api/auth';
import { connectSocket, disconnectSocket } from '@/lib/socket';
import { initPushNotifications, unsubscribePush } from '@/lib/pushSubscription';
import { ensureIdentityPublished } from '@/lib/secretChat/session';
import { clearIdbCache } from '@/lib/idbStorage';
import { enrollDevice, tryDeviceLogin, clearDeviceCredential, ensureDeviceV2 } from '@/lib/deviceAuth';
import { walletApi } from '@/api/wallet';
import { apiClient } from '@/api/client';
import { setAccessTokenMem, clearAccessTokenMem } from '@/api/tokenStore';

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;

  login: (emailOrUsername: string, password: string, totpCode?: string) => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: AuthUser) => void;
  setAccessToken: (token: string) => void;
  setSession: (accessToken: string, user: AuthUser) => void;
  initialize: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      isLoading: true,
      isAuthenticated: false,

      setUser: (user) => set({ user }),
      setAccessToken: (accessToken) => {
        setAccessTokenMem(accessToken);
        set({ accessToken });
      },

      setSession: (accessToken, user) => {
        setAccessTokenMem(accessToken);
        set({ user, accessToken, isAuthenticated: true });
        connectSocket(accessToken);
        initPushNotifications().catch(() => {});
        ensureIdentityPublished().catch(() => {});
      },

      login: async (emailOrUsername, password, totpCode) => {
        const data = await authApi.login({ emailOrUsername, password, totpCode });
        setAccessTokenMem(data.accessToken);
        set({ user: data.user, accessToken: data.accessToken, isAuthenticated: true });
        connectSocket(data.accessToken);
        initPushNotifications().catch(() => {});
        ensureIdentityPublished().catch(() => {});
        // Login-ответ НЕ содержит karma (её отдаёт только /me) → karma-гейтнутые
        // блоки (напр. майнинг RelayPanel в Settings: `user.karma >= 0xF4240`)
        // были бы спрятаны до первой перезагрузки. Догружаем полный профиль и
        // обновляем user — блок появляется сразу после входа. Fire-and-forget.
        authApi.me().then((full) => set({ user: full })).catch(() => {});
        // v5.9 — enroll this device for passwordless persistent auth on
        // subsequent visits. Fire-and-forget; failure не блокирует login.
        enrollDevice(apiClient).catch(() => {});
        // Phase 2 — signed wallet: пароль доступен → restore из бэкапа (новое
        // устройство) или generate+enroll+backup (первое). Fire-and-forget.
        walletApi.ensureWallet(data.user.id, password).catch(() => {});
      },

      logout: async () => {
        try {
          await unsubscribePush();
        } catch {}
        try {
          await authApi.logout();
        } catch {
          /* ignore */
        }
        clearAccessTokenMem();
        localStorage.removeItem('accessToken'); // подчистка legacy-ключа от старых версий
        disconnectSocket();
        // Phase 15.1 — чистим IDB-кэш чатов, чтобы следующий пользователь
        // на этом браузере не увидел наши каналы/сообщения.
        await clearIdbCache();
        // v5.9 — также чистим device credential. Если юзер делает explicit
        // logout — он хочет полный выход, не auto-relogin через device key.
        await clearDeviceCredential();
        set({ user: null, accessToken: null, isAuthenticated: false });
      },

      initialize: async () => {
        set({ isLoading: true });

        // Защита от «вечного загрузчика»: на iOS PWA после свайп-закрытия и
        // релонча отдельные fetch/IndexedDB-операции могут зависнуть и не
        // зарезолвиться — тогда initialize() висел бы вечно на спиннере
        // (бордовый SwipaLoader). Гард: что бы ни случилось, через 8с
        // снимаем загрузку и показываем экран входа. `settled` не даёт
        // позднему (уже бесполезному) ответу перезаписать состояние.
        let settled = false;
        const finish = (partial: Partial<AuthState>) => {
          if (settled) return;
          settled = true;
          set({ isLoading: false, ...partial } as Partial<AuthState>);
        };
        const guard = setTimeout(() => {
          clearAccessTokenMem();
          finish({ user: null, accessToken: null, isAuthenticated: false });
        }, 8000);

        // Каждый сетевой await оборачиваем гонкой с таймаутом, чтобы повисший
        // запрос (iOS) точно отклонился, а не держал промис открытым.
        const withTimeout = <T,>(p: Promise<T>, ms = 6000): Promise<T> =>
          Promise.race([
            p,
            new Promise<T>((_, rej) => setTimeout(() => rej(new Error('timeout')), ms)),
          ]);

        const activate = async (accessToken: string) => {
          setAccessTokenMem(accessToken);
          const user = await withTimeout(authApi.me());
          if (settled) return;
          finish({ user, accessToken, isAuthenticated: true });
          connectSocket(accessToken);
          initPushNotifications().catch(() => {});
          ensureIdentityPublished().catch(() => {});
          // #1 Piece B: апгрейд device-cred до v2 (non-extractable key) без разлогина.
          ensureDeviceV2(apiClient).catch(() => {});
          // Phase 2 — signed wallet: без пароля только enroll'им существующий
          // IDB-ключ (no-op, если ключа нет — дождёмся следующего login).
          walletApi.ensureWallet(user.id).catch(() => {});
        };

        try {
          // SECURITY (#1): access-токен больше не персистится в localStorage —
          // на старте его в памяти нет, поэтому идём сразу в refresh-cookie.
          // Чистим legacy-ключ от прошлых версий (плейнтекст-токен в localStorage).
          localStorage.removeItem('accessToken');

          try {
            const { accessToken } = await withTimeout(authApi.refresh());
            await activate(accessToken);
            if (settled) return;
          } catch {
            // Refresh cookie мёртв/отсутствует. Pass through to device fallback.
          }

          // v5.9 — fallback на device-bound persistent auth (если есть IDB-cred).
          try {
            const devLogin = await withTimeout(tryDeviceLogin(apiClient));
            if (devLogin && !settled) {
              setAccessTokenMem(devLogin.accessToken);
              finish({
                user: devLogin.user as AuthUser,
                accessToken: devLogin.accessToken,
                isAuthenticated: true,
              });
              connectSocket(devLogin.accessToken);
              initPushNotifications().catch(() => {});
              ensureIdentityPublished().catch(() => {});
              // #1 Piece B: апгрейд legacy v1-secret до v2 без разлогина.
              ensureDeviceV2(apiClient).catch(() => {});
              return;
            }
          } catch {
            /* fall through */
          }

          clearAccessTokenMem();
          finish({ user: null, accessToken: null, isAuthenticated: false });
        } finally {
          clearTimeout(guard);
        }
      },
    }),
    {
      name: 'bogema-auth',
      // SECURITY (#1): больше НЕ персистим access-токен в localStorage.
      // Сессия переживает reload через httpOnly refresh-cookie + device-cred.
      partialize: () => ({}),
    },
  ),
);
